import { z } from 'zod';
import { isoDateTimeSchema, uuidSchema } from './common.js';
/**
 * Shared wire schemas for `GET /admin/audit` and
 * `GET /admin/audit/export.csv` (TASK-092, admin-arch §3.11 + PRD §10.2).
 *
 * The audit log itself is written by `AuditLogService` / `AuditInterceptor`
 * — this file only covers the READ surface used by super-admins and (with
 * a tighter scope) selectors.
 */
/**
 * Row shape returned by the list endpoint. Mirrors the projection
 * documented in admin-arch §10 (`internal.audit_log` columns).
 *
 * `changes` is `unknown` on the wire because the diff payload varies by
 * action (track patch diff vs. login event vs. impersonate). Consumers
 * narrow it client-side. `actor_user_id` / `actor_role` are nullable to
 * cover system-driven actions (workers, cron) that have no human actor.
 */
export const auditLogListItemSchema = z.object({
    id: uuidSchema,
    entity_type: z.string(),
    entity_id: uuidSchema.nullable(),
    actor_user_id: uuidSchema.nullable(),
    actor_role: z.string().nullable(),
    action: z.string(),
    changes: z.unknown().nullable(),
    ip_address: z.string().nullable(),
    user_agent: z.string().nullable(),
    occurred_at: isoDateTimeSchema,
    // Wave 2.11 TASK-091c — cascade chain link. NULL for top-level rows or
    // for events emitted outside a request scope (cron / worker). When set,
    // points to the id of the parent audit row that triggered this child.
    parent_audit_id: uuidSchema.nullable(),
    /**
     * TASK-465: human-readable actor label resolved server-side via
     * `LEFT JOIN internal.users u ON u.id = audit_log.actor_user_id`. Same
     * convention as `trackCardAuditEntrySchema.actor_display_name`:
     *   - `null`                      — system actor (`actor_user_id` is null)
     *   - `'<удалённый пользователь>'` — the actor row is soft-deleted or gone
     *                                   (an audit row outlives the account)
     *   - the user's `name` otherwise
     */
    actor_display_name: z.string().nullable(),
});
/**
 * List response envelope. Keyset cursor pattern mirrors
 * `trackListResponseSchema`: `next_cursor` is `null` on the last page;
 * `total` is null in keyset mode (counts are expensive on append-only
 * audit log at scale).
 */
export const auditLogListResponseSchema = z.object({
    items: z.array(auditLogListItemSchema),
    total: z.number().int().nonnegative().nullable(),
    next_cursor: z.string().nullable(),
});
/**
 * Caps and bounds for query inputs. Audit log queries hit a JSONB column
 * via ILIKE on `changes::text` and may scan up to 1y of rows — every
 * upper bound here is defensive against pathological inputs that could
 * pin a worker thread (review I-1 / I-2 across BE waves).
 */
const ENTITY_TYPE_MAX = 64;
const ACTION_MAX = 128;
const Q_MAX = 200;
const IP_MAX = 45; // matches internal.audit_log.ip_address column length
const CURSOR_MAX = 200;
const ENTITY_TYPE_IN_MAX = 20; // small whitelist on the wire, matches FE chips
const ACTION_NOT_IN_MAX = 20; // same bound as `entity_type_in`
// Production carries 53 distinct actions (measured 2026-08-10); the picker
// must let an operator select all of them without hitting the cap.
const ACTION_IN_MAX = 100;
/**
 * Actions the role guards journal on EVERY granted request to a privileged
 * surface (`AuditedRoleGuard`, `SuperAdminGuard`). They are a security trail,
 * not human-facing activity: TASK-457 put `privileged_admin.access` on 8 GET
 * endpoints, and `super_admin.access` rows are still in the table from before
 * the guard was retired — together they bury real events in a "recent
 * activity" list (BUG-043 / BUG-049).
 *
 * Consumers that render a human feed pass this list as `action_not_in`. The
 * rows stay in `internal.audit_log` and remain visible on `/audit`.
 *
 * The matching `*.access_denied` actions are deliberately NOT here — a
 * rejected privilege escalation IS meaningful activity.
 */
export const AUDIT_ACCESS_NOISE_ACTIONS = [
    'privileged_admin.access',
    'super_admin.access',
];
/**
 * A bulk operation writes FOUR rows per run and only one of them is the
 * event a human means: `tracks.bulk_<kind>_requested` carries the operator's
 * intent (and, for a delete, `changes.track_ids`). The other three are
 * machine bookkeeping emitted by the runner:
 *
 *   `tracks.bulk_<kind>_submitted`      — the queued job envelope (counts)
 *   `tracks.bulk_<kind>_chunk_applied`  — one per chunk (first/last id)
 *   `tracks.bulk_<kind>_completed`      — terminal counters
 *   `tracks.bulk_<kind>_failed`         — terminal counters (fatal path)
 *
 * `<kind>` is one of {@link bulkJobKindSchema} and the action strings are
 * built by template literal in `bulk.service.ts` / `bulk-job-runner.
 * service.ts`, so an explicit exclusion list would need 7 × 4 = 28 entries
 * and would silently go stale the moment a new kind is added. The pattern
 * below is matched server-side with a POSIX regex instead — kind-agnostic
 * by construction.
 *
 * Exported so tests can assert the FE label map and the BE predicate agree
 * on the same shape.
 */
export const AUDIT_BULK_ANCHOR_SUFFIX = 'requested';
export const AUDIT_BULK_DERIVED_SUFFIXES = [
    'submitted',
    'chunk_applied',
    'completed',
    'failed',
];
/**
 * POSIX regex (Postgres `!~` operand) matching the derivative rows above.
 * `_` is not a regex metacharacter, so no escaping is needed; the `\.` is.
 */
export const AUDIT_BULK_DERIVED_ACTION_PATTERN = `^tracks\\.bulk_.+_(${AUDIT_BULK_DERIVED_SUFFIXES.join('|')})$`;
const csvStringArraySchema = (max, itemMax) => z.preprocess((raw) => {
    if (raw === undefined || raw === null || raw === '')
        return undefined;
    if (Array.isArray(raw))
        return raw;
    if (typeof raw === 'string') {
        return raw
            .split(',')
            .map((s) => s.trim())
            .filter((s) => s.length > 0);
    }
    return raw;
}, z.array(z.string().min(1).max(itemMax)).max(max).optional());
/**
 * Query schema for `GET /admin/audit` AND `GET /admin/audit/export.csv`
 * (the export uses the SAME filter shape, just a different serialiser).
 *
 * - `entity_type_in` accepts CSV (`track,tag`) or array (`?entity_type=track&entity_type=tag`).
 *   Single value `entity_type` is also accepted via the same schema.
 * - `q` is a free-text ILIKE against `changes::text` — capped at 200 chars
 *   to avoid GIN trigram blowup.
 * - `cursor` carries the opaque base64 keyset token minted by the API.
 * - `limit` caps at 200 (same as `trackFilterSchema.size`).
 *
 * Selector scope (admin-arch §3.11): the service silently overrides
 * `actor_user_id` to the authenticated user — the wire schema accepts the
 * field but the role guard short-circuits an attempt to pass another id.
 */
export const auditLogFilterSchema = z.object({
    entity_type: z.string().min(1).max(ENTITY_TYPE_MAX).optional(),
    entity_type_in: csvStringArraySchema(ENTITY_TYPE_IN_MAX, ENTITY_TYPE_MAX),
    entity_id: uuidSchema.optional(),
    actor_user_id: uuidSchema.optional(),
    action: z.string().min(1).max(ACTION_MAX).optional(),
    /**
     * TASK-520: multi-select action filter, CSV (`a,b`) or repeated param.
     * Mirrors `entity_type_in`. Added because the single `action` field is an
     * EXACT match and the admin UI offered no way to discover a valid value
     * (BUG-268) — an operator hunting «what was deleted» has to be able to
     * pick several related actions at once.
     *
     * Takes precedence over the scalar `action` when both are present.
     */
    action_in: csvStringArraySchema(ACTION_IN_MAX, ACTION_MAX),
    /**
     * TASK-465: exclusion list, CSV (`a,b`) or repeated param. Complements the
     * positive `action` filter — the dashboard feed passes
     * {@link AUDIT_ACCESS_NOISE_ACTIONS} so guard bookkeeping does not bury
     * real events. Empty / absent means "exclude nothing".
     */
    action_not_in: csvStringArraySchema(ACTION_NOT_IN_MAX, ACTION_MAX),
    /**
     * TASK-522: when true, the derivative bookkeeping rows of a bulk operation
     * are hidden and only the operator-intent row survives — one journal line
     * per operation instead of four (see
     * {@link AUDIT_BULK_DERIVED_ACTION_PATTERN}). Defaults to FALSE so every
     * existing consumer keeps seeing the raw trail; the admin journal opts in.
     */
    collapse_bulk: z
        .union([z.literal('true'), z.literal('false'), z.boolean()])
        .transform((v) => (typeof v === 'boolean' ? v : v === 'true'))
        .optional(),
    date_from: isoDateTimeSchema.optional(),
    date_to: isoDateTimeSchema.optional(),
    q: z.string().min(1).max(Q_MAX).optional(),
    ip: z.string().min(1).max(IP_MAX).optional(),
    limit: z.coerce.number().int().positive().max(200).default(50),
    cursor: z.string().max(CURSOR_MAX).optional(),
});
// ---------------------------------------------------------------------------
// Action catalogue — `GET /admin/audit/actions` (TASK-520, BUG-268)
// ---------------------------------------------------------------------------
/**
 * One selectable action for the journal's action picker. The set is derived
 * from the DATA (`SELECT DISTINCT action FROM internal.audit_log`), not from
 * a hand-maintained enum: the writer side builds action strings by template
 * literal (`tracks.bulk_${kind}_completed`), so any static list would drift.
 *
 * `count` and `last_occurred_at` are what turn the picker from a list of
 * strings into an answer — «tracks.bulk_delete_requested · 226 · 07.08» tells
 * the operator the deletions exist BEFORE they filter, which is precisely
 * what the bare text input could not do.
 */
export const auditActionOptionSchema = z.object({
    action: z.string(),
    count: z.number().int().nonnegative(),
    last_occurred_at: isoDateTimeSchema,
});
export const auditActionCatalogueSchema = z.object({
    items: z.array(auditActionOptionSchema),
});
// ---------------------------------------------------------------------------
// Entry detail — `GET /admin/audit/:id` (TASK-521, BUG-269)
// ---------------------------------------------------------------------------
/**
 * A referenced entity resolved to something a human recognises.
 *
 * `deleted` is part of the contract, not decoration: every id a
 * `tracks.bulk_delete_requested` row points at is BY DEFINITION soft-deleted,
 * so a resolver that filtered `deleted_at IS NULL` would return an empty set
 * and the UI would render a column of blanks (the BUG-102 failure mode). The
 * flag makes the "yes, this row is gone" state explicit instead of implicit.
 */
export const auditEntityRefSchema = z.object({
    entity_type: z.string(),
    id: uuidSchema,
    /** Track title, tag name, … — never null; falls back to the id server-side. */
    title: z.string(),
    /** Secondary line (artist for a track). Null when the entity has none. */
    subtitle: z.string().nullable(),
    /** True when the row is soft-deleted (`deleted_at IS NOT NULL`). */
    deleted: z.boolean(),
});
/** Upper bound on resolved entities in one detail response. */
export const AUDIT_ENTITY_RESOLVE_MAX = 500;
/**
 * Detail payload behind one journal row.
 *
 * `related` collapses the bulk-operation fan-out: the four rows a bulk run
 * writes share `entity_id` (the `internal.bulk_jobs.id`), so the drawer can
 * show ONE event with its lifecycle underneath instead of making the operator
 * scroll through four table rows. Empty for ordinary single-entity events.
 */
export const auditLogEntryDetailSchema = z.object({
    entry: auditLogListItemSchema,
    related: z.array(auditLogListItemSchema),
    entities: z.array(auditEntityRefSchema),
    /** True when the event referenced more ids than {@link AUDIT_ENTITY_RESOLVE_MAX}. */
    entities_truncated: z.boolean(),
});
//# sourceMappingURL=audit.js.map